import { User, UserInfoApi } from "@repo/baseCloud";
import { hasValue } from "@repo/helpers";
import { useQuery } from "@tanstack/react-query";
import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";

interface UserContextType {
	user?: User;
	loggedIn?: boolean;
	updateUser: (user?: Partial<User>) => void;
	onAuthError: (err?: string) => void;
	loading: boolean;
}

export const UserContext = createContext<UserContextType>({
	user: undefined,
	updateUser: () => undefined,
	onAuthError: (err?: string) => undefined,
	loggedIn: false,
	loading: false,
});

interface UserProviderProps {
	children: ReactNode;
}

export function useUserContext() {
	return useContext(UserContext);
}

export const UserProvider: FC<UserProviderProps> = ({ children }) => {
	const timeoutRef = useRef(0);

	function checkUserChanged(forceStart = false) {
		if (!forceStart && !timeoutRef.current) return;
		if (!isUserStored()) setUser({} as User);

		timeoutRef.current = setTimeout(checkUserChanged, 25) as any;
	}
	const [user, setUser] = useState<User>(getStoredUser);
	const loggedIn = user.loggedIn;

	const userInfoQuery = useQuery({
		queryKey: ["GetUserInfo"],
		queryFn: UserInfoApi.getUserInfo,
	});

	useEffect(() => {
		setStoredUser(user);
		checkUserChanged(true);
	}, [user]);

	useEffect(() => {
		if (userInfoQuery.isLoading) return;
		if (!hasValue(userInfoQuery.data)) return;

		setUser({ ...userInfoQuery.data, loggedIn: true });
	}, [userInfoQuery.isLoading, userInfoQuery.data]);

	const onAuthError = useCallback(
		function (loginError?: string) {
			clearStoredUser();
			setUser({ loginError } as User);
		},
		[setUser],
	);

	function updateUser(newUser: User) {
		timeoutRef.current = null;
		setUser({ ...user, ...newUser });
	}

	return (
		<UserContext.Provider
			value={{
				user,
				updateUser,
				onAuthError,
				loggedIn,
				loading: userInfoQuery.isLoading,
			}}
		>
			{children}
		</UserContext.Provider>
	);
};

const userNameStorage = `TLPCloud_LastLoggedInUsername`;
const userStorage = `TLPCloud_User`;
function setStoredUser(user: User) {
	localStorage.setItem(userStorage, JSON.stringify(user));
}
export function clearStoredUser() {
	localStorage.removeItem(userStorage);
}

function isUserStored() {
	return !!localStorage.getItem(userStorage);
}
function getStoredUser() {
	const json = localStorage.getItem(userStorage);
	const storedValue = json && (JSON.parse(json) as User);
	return storedValue ?? defaultUser;
}
export function storeUserName(username: string) {
	localStorage.setItem(userNameStorage, username);
}
export function clearUserName() {
	localStorage.removeItem(userNameStorage);
}
export function getStoredUserName() {
	return localStorage.getItem(userNameStorage);
}

const defaultUser = {
	userId: null,
	userName: null,
	userIsAdmin: false,
	userIsVendorUser: false,
	userIsVendorAdmin: false,
	loggedIn: false,
	loginError: null,
	loading: true,
} as User;
